<template>
  <div class="carrier-plan-selector" v-loading="loadingCargoValue">
    <div class="option-list" v-for="(option, index) in options" :key="index">
      <div
        class="option-selector"
        :class="{
          active: plan === option.value,
          disabled: isOptionDisabled(option.value),
        }"
        @click="selectedPlan(option.value)"
      >
        <div class="image-container">
          <svg
            v-if="isShieldOption(option.value)"
            width="52"
            height="56"
            viewBox="0 0 52 56"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M26 0C26.4983 0 26.9967 0.109976 27.4517 0.318932L47.8508 9.10605C50.2341 10.1288 52.0108 12.5153 51.9999 15.3967C51.9458 26.3064 47.5258 46.2671 28.86 55.3401C27.0508 56.22 24.9492 56.22 23.14 55.3401C4.47421 46.2671 0.0542192 26.3064 4.92453e-05 15.3967C-0.0107808 12.5153 1.76588 10.1288 4.14921 9.10605L24.5592 0.318932C25.0033 0.109976 25.5017 0 26 0Z"
              fill="#043663"
            />
            <text
              x="26"
              y="33"
              text-anchor="middle"
              fill="white"
              font-size="13"
              font-weight="700"
              font-family="Arial, sans-serif"
            >
              {{ getShieldLabel(option.value) }}
            </text>
            <rect
              x="14"
              y="36.8628"
              width="27"
              height="1.21224"
              rx="0.606119"
              transform="rotate(-8.22534 14 36.8628)"
              fill="white"
            />
            <rect
              x="27"
              y="38.0416"
              width="14.27"
              height="1.2384"
              rx="0.619202"
              transform="rotate(-8.22534 27 38.0416)"
              fill="white"
            />
          </svg>
          <BasicPlanIcon v-else />
        </div>

        <div class="option-information">
          <h5 class="option-information__title">{{ option.title }}</h5>
          <span class="option-information__text">{{ option.text }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BasicPlanIcon from "./BasicPlanIcon.vue";

export default {
  name: "CarrierPlanSelector",
  props: {
    loadingCargoValue: Boolean,
    disableButtons: Boolean,
    cargoValue: Number,
    initialPlan: {
      type: String,
      default: "",
    },
  },
  components: {
    BasicPlanIcon,
  },
  data() {
    return {
      plan: "minimum",
    };
  },
  computed: {
    normalizedCargoValue() {
      const n = Number(this.cargoValue);
      if (Number.isNaN(n) || n < 0) return 0;
      return n;
    },
    allowMinimum() {
      return this.normalizedCargoValue <= 15000;
    },
    allowBasic() {
      return this.normalizedCargoValue > 15000 && this.normalizedCargoValue <= 25000;
    },
    options() {
      return [
        {
          value: "minimum",
          title: this.$t("carriers.coveragePlans.minimum.title"),
          text: this.$t("carriers.coveragePlans.minimum.text"),
        },
        {
          value: "basic",
          title: this.$t("carriers.coveragePlans.basic.title"),
          text: this.$t("carriers.coveragePlans.basic.text"),
        },
        {
          value: "insurance",
          title: this.$t("carriers.coveragePlans.quoteFull.title"),
          text: this.$t("carriers.coveragePlans.quoteFull.text"),
        },
      ];
    },
  },
  watch: {
    initialPlan: {
      immediate: true,
      handler(next) {
        const desired = `${next || ""}`.trim();
        if (!desired) {
          this.plan = this.normalizedCargoValue > 0 ? "insurance" : "minimum";
          this.ensurePlanAllowed();
          return;
        }
        this.plan = desired;
        this.ensurePlanAllowed();
      },
    },
    normalizedCargoValue() {
      this.ensurePlanAllowed();
    },
  },
  methods: {
    isShieldOption(value) {
      return value === "minimum" || value === "insurance";
    },
    getShieldLabel(value) {
      return value === "minimum" ? "min" : "100";
    },
    isOptionDisabled(value) {
      if (this.loadingCargoValue || this.disableButtons) return true;
      if (value === "minimum") return !this.allowMinimum;
      if (value === "basic") return !this.allowBasic;
      return false;
    },
    setPlan(next, shouldEmit) {
      const normalized = `${next || ""}`.trim();
      if (!normalized) return;
      this.plan = normalized;
      if (shouldEmit) {
        this.$emit("handleSelectedPlan", this.plan);
      }
    },
    ensurePlanAllowed() {
      if (this.plan === "minimum" && !this.allowMinimum) {
        const next = this.allowBasic ? "basic" : "insurance";
        if (next !== this.plan) this.setPlan(next, true);
        return;
      }
      if (this.plan === "basic" && !this.allowBasic) {
        const next = this.allowMinimum ? "minimum" : "insurance";
        if (next !== this.plan) this.setPlan(next, true);
        return;
      }
      const known = this.plan === "minimum" || this.plan === "basic" || this.plan === "insurance";
      if (!known) {
        this.setPlan(this.normalizedCargoValue > 0 ? "insurance" : "minimum", true);
      }
    },
    selectedPlan(plan) {
      if (this.isOptionDisabled(plan)) return;
      this.setPlan(plan, true);
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/_themes' as *;
@use '@/assets/scss/_variables' as *;
.carrier-plan-selector {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  width: 100%;
}
.option-list {
  min-width: 0;
}
.option-selector {
  display: flex;
  padding: 10px 12px;
  margin: 0;
  align-items: center;
  position: relative;
  width: 100%;
  min-height: 86px;
  border: 2px solid rgba(128, 128, 128, 0.242);
  border-radius: 8px;
  cursor: pointer;
  box-sizing: border-box;
  overflow: hidden;
  input {
    position: absolute;
    top: 10px;
    right: 10px;
  }
}
.image-container {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 56px;
}
::v-deep .image-container svg {
  width: 56px;
  height: 56px;
  display: block;
}
.active {
  // Ask sebas. background: lighten($color-border-container, 20%);
  background: #a5dae024;
  border-color: $color-border-container;
  input[type="radio"] {
    accent-color: $color-border-container;
  }
}
.option-information {
  min-width: 0;
  color: $color-primary-company;
  line-height: 15px;
  padding-left: 10px;
  &__title {
    font-weight: bold;
    font-size: 15px;
    margin: 0px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__text {
    font-weight: normal;
    font-size: 12px;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.disabled {
  opacity: 0.5;
  pointer-events: none;
}

::v-deep {
  .el-loading-mask {
    border-radius: 8px;
  }
}

@media (max-width: 800px) {
  .carrier-plan-selector {
    grid-template-columns: 1fr;
  }
  .option-selector {
    margin: 0;
  }
}
</style>
